// Shared helpers for the ticker element, used by both the React builder
// preview (ElementTicker.js) and the live-site renderer (_ticker.js).
class TickerService {
  // Return the first value in `vals` that parses to a finite number >= `min`.
  // Coerces "", null, undefined, and NaN to "unset" so they fall through to
  // the next cascade entry. Callers that disallow 0 (e.g. speed) pass min=1.
  static resolveNumeric(vals, min = 0) {
    for (let i = 0; i < vals.length; i++) {
      const v = vals[i];
      if (v === undefined || v === null || v === "") continue;
      const n = parseFloat(v);
      if (!isNaN(n) && n >= min) return n;
    }
    return null;
  }

  // Resolve a ticker option with the standard cascade:
  //   element[`${key}-mobile`] → element[key] → variant[`${key}-mobile`]
  //   → variant[key] → fallback
  // On desktop, the `-mobile` keys are skipped.
  static resolveOption(elementOptions, variantOptions, key, device, fallback) {
    const eOpts = elementOptions || {};
    const vOpts = variantOptions || {};

    if (device === "mobile") {
      const val =
        eOpts[`${key}-mobile`] ||
        eOpts[key] ||
        vOpts[`${key}-mobile`] ||
        vOpts[key];
      if (val !== undefined && val !== null && val !== "") return val;
      return fallback;
    }

    const val = eOpts[key] || vOpts[key];
    if (val !== undefined && val !== null && val !== "") return val;
    return fallback;
  }

  // Resolve a ticker option expected to be a number >= `min` (default 0 so
  // that gap/border-width can accept 0). Explicit null-check on the result
  // so a valid 0 is preserved (`|| fallback` would replace it).
  static resolveNumericOption(
    elementOptions,
    variantOptions,
    key,
    device,
    fallback,
    min = 0
  ) {
    const eOpts = elementOptions || {};
    const vOpts = variantOptions || {};

    const vals =
      device === "mobile"
        ? [eOpts[`${key}-mobile`], eOpts[key], vOpts[`${key}-mobile`], vOpts[key]]
        : [eOpts[key], vOpts[key]];

    const resolved = TickerService.resolveNumeric(vals, min);
    return resolved === null ? fallback : resolved;
  }

  // Map `content_toggle_items` (array or dict, keyed by id) belonging to
  // `elementId` into `{ id, url, alt, link }[]` sorted by position.
  static imagesFromToggleItems(toggleItems, elementId) {
    if (!toggleItems) return [];
    const items = Array.isArray(toggleItems)
      ? toggleItems
      : Object.values(toggleItems);

    return items
      .filter(
        (item) =>
          item &&
          (elementId == null || item.element_id == elementId) &&
          item.toBeDeleted !== true
      )
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map((item) => ({
        id: item.id,
        url: item.content || "",
        alt: (item.options && item.options.alt) || "",
        link: (item.options && item.options.link) || "",
      }));
  }
}

if (typeof window !== "undefined") {
  window.TickerService = TickerService;
}

export default TickerService;
